import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Receipt, CheckCircle2, Clock, AlertCircle, XCircle, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ManualPaymentModal from "@/components/payments/ManualPaymentModal";

const STATUS_META = {
  pending_payment:      { label: "Awaiting your action", Icon: Clock, color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
  pending_verification: { label: "Verifying with admin", Icon: AlertCircle, color: "text-sky-300", bg: "bg-sky-500/10 border-sky-500/30" },
  approved:             { label: "Approved", Icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  rejected:             { label: "Not verified", Icon: XCircle, color: "text-rose-300", bg: "bg-rose-500/10 border-rose-500/30" },
  expired:              { label: "Expired", Icon: XCircle, color: "text-zinc-400", bg: "bg-zinc-500/10 border-zinc-500/30" },
};

function PaymentRow({ p }) {
  const meta = STATUS_META[p.status] || STATUS_META.pending_payment;
  const Icon = meta.Icon;
  const fmt = new Date(p.created_at).toLocaleString();
  return (
    <div data-testid={`payment-row-${p.id}`} className={`rounded-md border ${meta.bg} p-4 flex items-start gap-3`}>
      <Icon className={`w-5 h-5 ${meta.color} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-white font-medium">
            {p.currency} ${Number(p.amount).toLocaleString()} · {p.method.replace("_", " ")}
          </div>
          <div className={`text-xs ${meta.color}`}>{meta.label}</div>
        </div>
        <div className="text-xs text-white/50 mt-1">
          {p.purpose.replace("_", " ")}
          {p.note ? ` · ${p.note}` : ""}
        </div>
        <div className="text-[10px] text-white/40 mt-1">Ref {p.id.slice(0, 8)} · {fmt}</div>
        {p.rejection_reason && (
          <div className="text-xs text-rose-300 mt-2 italic">"{p.rejection_reason}"</div>
        )}
        {p.receipt_url && (
          <a
            href={p.receipt_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gold/80 hover:text-gold mt-2"
          >
            <Receipt className="w-3 h-3" /> View receipt
          </a>
        )}
      </div>
    </div>
  );
}

export default function PaymentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [amt, setAmt] = useState("");
  const [cur, setCur] = useState("JMD");
  const [purpose, setPurpose] = useState("other");
  const [refNote, setRefNote] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/payments/manual/me");
      setItems(r.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sign in to view payments");
        window.location.href = "/vip";
        return;
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="payments-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl text-gold font-display">My Payments</h1>
            <p className="text-sm text-white/60 mt-1">Bank, Lynk JA, cash, crypto — all in one place.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="payments-refresh"
              onClick={load}
              className="text-white/60 hover:text-gold border border-white/10 hover:border-gold/40 rounded-md p-2 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="new-payment-button"
              onClick={() => setShowForm((s) => !s)}
              className="inline-flex items-center gap-2 bg-gold text-black hover:bg-gold/90 rounded-md px-4 py-2 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> New payment
            </button>
          </div>
        </div>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="border border-gold/20 rounded-md p-4 mb-6 bg-black/30"
            data-testid="payments-form"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-white/50">Amount</label>
                <input
                  data-testid="payment-amount-input"
                  type="number" min="1" value={amt} onChange={(e) => setAmt(e.target.value)}
                  className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-xs text-white/50">Currency</label>
                <select
                  data-testid="payment-currency-input"
                  value={cur} onChange={(e) => setCur(e.target.value)}
                  className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="JMD">JMD</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/50">Purpose</label>
                <select
                  data-testid="payment-purpose-input"
                  value={purpose} onChange={(e) => setPurpose(e.target.value)}
                  className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="other">Other</option>
                  <option value="chauffeur_ride">Chauffeur ride</option>
                  <option value="event_booking">Event booking</option>
                  <option value="deposit">Deposit</option>
                  <option value="ticket">Event ticket</option>
                  <option value="gig">Gig / project</option>
                  <option value="product">Product</option>
                  <option value="subscription">Subscription</option>
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1 flex items-end">
                <button
                  data-testid="payment-continue-button"
                  onClick={() => {
                    if (!amt || Number(amt) <= 0) { toast.error("Enter an amount"); return; }
                    setShowForm(false);
                    setShowModal(true);
                  }}
                  className="w-full bg-gold/10 text-gold border border-gold/40 hover:bg-gold/20 rounded-md py-2 text-sm transition-colors"
                >
                  Continue →
                </button>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-white/50">Reference (optional)</label>
              <input
                data-testid="pay-form-note"
                type="text" value={refNote} onChange={(e) => setRefNote(e.target.value)}
                placeholder="e.g. Yacht Gala — Sarah Brown"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center text-white/50 py-16">Loading…</div>
        ) : items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="text-center border border-dashed border-gold/20 rounded-md py-12 px-6"
          >
            <Receipt className="w-10 h-10 text-gold/60 mx-auto mb-3" />
            <div className="text-white text-lg font-display">No payments yet</div>
            <div className="text-sm text-white/60 mt-1">
              Make a payment for a ride, an event booking, or any service. Bank, Lynk JA, cash, and crypto are all supported.
            </div>
            <Link
              to="/chauffeur"
              className="inline-block mt-4 text-xs text-gold/80 hover:text-gold border border-gold/30 hover:border-gold rounded-md px-3 py-1.5 transition-colors"
            >
              Browse chauffeur services →
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {items.map((p) => <PaymentRow key={p.id} p={p} />)}
          </div>
        )}
      </div>

      <ManualPaymentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        amount={Number(amt) || 0}
        currency={cur}
        purpose={purpose}
        defaultNote={refNote}
        onApproved={() => { setShowModal(false); setAmt(""); setRefNote(""); load(); }}
      />
    </div>
  );
}
